/**
 * Manual radar-plotting math. Pure functions, no React, no canvas.
 *
 * The mental model: own ship at the origin of an XY plane (nautical miles,
 * east = +x, north = +y). Target plots come in as (bearing, range, time);
 * we project to XY and reason about relative motion, CPA, TCPA, and OAW
 * (own/apparent/wind) velocity triangles.
 */

export interface Plot {
  /** Free-form label, e.g. "T1" for first fix, "T2" for second. */
  label: string;
  /** Vessel/target id this plot relates to. CPA uses pairs of same id. */
  targetId: string;
  /** True bearing from own ship, degrees. */
  bearingDeg: number;
  /** Range in nautical miles from own ship. */
  rangeNm: number;
  /** Sim time of the plot, seconds since scenario start. */
  simSeconds: number;
}

export interface XY {
  x: number;
  y: number;
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const SECONDS_PER_HOUR = 3600;

/** Convert a (bearing,range) pair to XY in nm with own ship at origin. */
export function plotToXY(p: Pick<Plot, "bearingDeg" | "rangeNm">): XY {
  const rad = p.bearingDeg * DEG_TO_RAD;
  return { x: p.rangeNm * Math.sin(rad), y: p.rangeNm * Math.cos(rad) };
}

export interface RelativeMotion {
  /** Relative course from p1 toward p2 in degrees true. */
  relativeCourseDeg: number;
  /** Relative speed in knots. */
  relativeSpeedKnots: number;
  /** Closest point of approach in nautical miles from own. */
  cpaNm: number;
  /** Time to CPA from p2's time, in minutes. Negative = past. */
  tcpaMin: number;
}

/**
 * Compute relative motion + CPA/TCPA from two plots of the same target.
 * Output is geometrically equivalent to engine.computeCPA but operates on
 * two observed plots rather than direct course/speed inputs — i.e. the
 * student's view of the world.
 */
export function relativeMotionFromPlots(p1: Plot, p2: Plot): RelativeMotion {
  if (p1.targetId !== p2.targetId) {
    throw new Error("plots must be of the same target");
  }
  if (p2.simSeconds <= p1.simSeconds) {
    throw new Error("p2 must be after p1");
  }
  const a = plotToXY(p1);
  const b = plotToXY(p2);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dt = (p2.simSeconds - p1.simSeconds) / SECONDS_PER_HOUR;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const relativeSpeedKnots = dist / dt;
  const relativeCourseDeg = ((Math.atan2(dx, dy) * RAD_TO_DEG) + 360) % 360;

  // Find perpendicular from origin (own) to the line through (a,b).
  // Using vector projection: t = -(a·d)/(d·d), then closest point = a + t*d.
  const denom = dx * dx + dy * dy;
  if (denom === 0) {
    return {
      relativeCourseDeg: 0,
      relativeSpeedKnots: 0,
      cpaNm: Math.sqrt(a.x * a.x + a.y * a.y),
      tcpaMin: Number.POSITIVE_INFINITY,
    };
  }
  const t = -(a.x * dx + a.y * dy) / denom;
  const closestX = a.x + t * dx;
  const closestY = a.y + t * dy;
  const cpaNm = Math.sqrt(closestX * closestX + closestY * closestY);

  // tcpa: t parameter in plot-units (0=p1, 1=p2). After p2, value is (t-1).
  const tcpaUnitsFromP2 = t - 1;
  const tcpaSeconds =
    tcpaUnitsFromP2 * (p2.simSeconds - p1.simSeconds);
  return {
    relativeCourseDeg,
    relativeSpeedKnots,
    cpaNm,
    tcpaMin: tcpaSeconds / 60,
  };
}

export interface OAWInput {
  /** Own course over ground in degrees true. */
  ownCourseDeg: number;
  /** Own speed over ground in knots. */
  ownSpeedKnots: number;
  /** True wind direction (where wind is FROM) in degrees. */
  trueWindFromDeg: number;
  /** True wind speed in knots. */
  trueWindSpeedKnots: number;
}

export interface OAWOutput {
  /** Apparent wind direction (where wind appears to come FROM) in degrees. */
  apparentWindFromDeg: number;
  /** Apparent wind speed in knots. */
  apparentWindSpeedKnots: number;
}

/**
 * OAW velocity triangle for sailing-vessel teaching: given own course/speed
 * and true wind, compute the apparent wind. The vector identity is:
 *   apparent = true - own_motion (both as TO vectors)
 * where "true wind TO" is opposite the FROM bearing.
 */
export function oawTriangle(input: OAWInput): OAWOutput {
  const ownToRad = input.ownCourseDeg * DEG_TO_RAD;
  const ownVx = input.ownSpeedKnots * Math.sin(ownToRad);
  const ownVy = input.ownSpeedKnots * Math.cos(ownToRad);

  // True wind FROM bearing → wind vector points toward (FROM + 180).
  const trueToBearing = (input.trueWindFromDeg + 180) % 360;
  const trueRad = trueToBearing * DEG_TO_RAD;
  const trueVx = input.trueWindSpeedKnots * Math.sin(trueRad);
  const trueVy = input.trueWindSpeedKnots * Math.cos(trueRad);

  // Apparent motion of air relative to vessel = true - own.
  const ax = trueVx - ownVx;
  const ay = trueVy - ownVy;
  const apparentToBearing = ((Math.atan2(ax, ay) * RAD_TO_DEG) + 360) % 360;
  const apparentFrom = (apparentToBearing + 180) % 360;
  const apparentSpeed = Math.sqrt(ax * ax + ay * ay);
  return {
    apparentWindFromDeg: apparentFrom,
    apparentWindSpeedKnots: apparentSpeed,
  };
}
